import React, { useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./NewGlobalDataTable.css";

const NewDataTable = ({
    columns,
    data,
    tableName,
    defaultRowsPerPage = 10, // 👈 default if not passed
}) => {
    data = Array.isArray(data) ? data : [];

    const [searchText, setSearchText] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [columnWidths, setColumnWidths] = useState({});

    const handleSearch = (e) => setSearchText(e.target.value);

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setPage(1);
    };

    const handlePageChange = (direction) => {
        setPage((prev) => Math.max(1, prev + direction));
    };

    const handleSort = (columnIndex) => {
        const key = columns[columnIndex].name;
        const direction =
            sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ key, direction });
    };

    const startResize = (e, index) => {
        e.preventDefault();
        const startX = e.clientX;
        const th = e.target.parentElement;
        const startWidth = th.offsetWidth;

        const handleMouseMove = (moveEvent) => {
            const newWidth = Math.max(60, startWidth + moveEvent.clientX - startX);
            setColumnWidths((prev) => ({ ...prev, [index]: `${newWidth}px` }));
        };

        const handleMouseUp = () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    // --- Filter & Sort ---
    const filteredData = useMemo(() => {
        let tempData = [...data];

        if (searchText) {
            tempData = tempData.filter((item) =>
                columns.some((col) => {
                    const val = col.selector?.(item);
                    return (
                        val &&
                        val.toString().toLowerCase().includes(searchText.toLowerCase())
                    );
                })
            );
        }

        if (sortConfig.key) {
            const col = columns.find((c) => c.name === sortConfig.key);
            if (col && col.sortable) {
                tempData = tempData.sort((a, b) => {
                    const valA = col.selector?.(a);
                    const valB = col.selector?.(b);
                    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
                    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
                    return 0;
                });
            }
        }

        return tempData;
    }, [data, searchText, sortConfig, columns]);

    // --- Pagination ---
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = filteredData.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    return (
        <div className="datatable-container p-3 border rounded">
            {/* --- Top Controls --- */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                {/* Left: Table name */}
                <h5 className="mb-0 fw-bold text-dark">{tableName}</h5>

                {/* Right: Search box */}
                <div>
                    <input
                        type="text"
                        value={searchText}
                        onChange={handleSearch}
                        placeholder="Search"
                        className="form-control form-control-sm"
                        style={{ minWidth: "200px" }}
                    />
                </div>
            </div>

            {/* --- Table --- */}
            <table className="table table-bordered table-hover table-striped table-sm align-middle">
                <thead className="table-primary text-center">
                    <tr>
                        {columns.map((col, index) => (
                            <th
                                key={index}
                                onClick={() => col.sortable && handleSort(index)}
                                style={{
                                    cursor: col.sortable ? "pointer" : "default",
                                    position: "relative",
                                    width: columnWidths[index] || "auto",
                                    textAlign: "center",
                                }}
                            >
                                {col.name}
                                {sortConfig.key === col.name && (
                                    <small className="ms-1">
                                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                                    </small>
                                )}
                                <div
                                    className="resize-handle"
                                    onMouseDown={(e) => startResize(e, index)}
                                />
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {paginatedData.length > 0 ? (
                        paginatedData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {columns.map((col, colIndex) => (
                                    <td
                                        key={colIndex}
                                        style={{
                                            width: columnWidths[colIndex] || "auto",
                                            wordBreak: "break-word",
                                            textAlign: col.align || "left",
                                        }}
                                    >
                                        {col.cell
                                            ? col.cell(row, rowIndex)
                                            : col.selector?.(row, rowIndex)}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={columns.length} className="text-muted text-center">
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* --- Bottom Controls --- */}
            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                {/* Bottom Left: Page size */}
                <div className="d-flex align-items-center">
                    <label className="me-2 fw-semibold text-secondary">Page Size:</label>
                    <select
                        value={rowsPerPage}
                        className="form-select form-select-sm w-auto"
                        onChange={handleRowsPerPageChange}
                    >
                        {
                            // Generate options dynamically — include defaultRowsPerPage if not in the preset list
                            [...new Set([defaultRowsPerPage, 10, 20, 30])]
                                .sort((a, b) => a - b)
                                .map((num) => (
                                    <option key={num} value={num}>
                                        {num}
                                    </option>
                                ))
                        }
                    </select>

                </div>

                {/* Bottom Right: Pagination */}
                <div>
                    Page {page} of {totalPages || 1}&nbsp;
                    <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        onClick={() => handlePageChange(-1)}
                        disabled={page === 1}
                    >
                        Previous
                    </button>
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handlePageChange(1)}
                        disabled={page === totalPages || totalPages === 0}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewDataTable;
